import { type SerializedStyles, css } from '@emotion/react';
import { Box, Icon, Text, Title, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { faFaceAnguished, faFaceSadSweat } from '@inflearn/pro-solid-svg-icons';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';

const PainPointsSection = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styleSection}>
      <Box css={styleContainer}>
        <Box css={styleContents}>
          <Title order={2} align="center" color="dark.9" css={styleTitle}>
            <Trans
              i18nKey="open.knowledge.pain.title"
              components={{
                br: (
                  <br
                    css={{
                      display: 'none',
                      [desktopFirstMediaQuery.mobile]: {
                        display: 'block',
                      },
                    }}
                  />
                ),
              }}
            />
          </Title>
          <Box css={styleBubblesWrapper}>
            <PainPoint
              style={styleBubble(PAIN_POINTS_STYLES[0])}
              icon={<Icon icon={faFaceSadSweat} css={styleIcon} color="orange.3" />}
            >
              {t('open.knowledge.pain.revenue')}
            </PainPoint>
            <PainPoint style={styleBubble(PAIN_POINTS_STYLES[1])}>
              {t('open.knowledge.pain.fee')}
            </PainPoint>
            <PainPoint
              style={styleBubble(PAIN_POINTS_STYLES[2])}
              icon={<Icon icon={faFaceAnguished} css={styleIcon} color="indigo.3" />}
            >
              {t('open.knowledge.pain.complexity')}
            </PainPoint>
            <PainPoint style={styleBubble(PAIN_POINTS_STYLES[3])}>
              {t('open.knowledge.pain.market')}
            </PainPoint>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const PainPoint = ({
  icon,
  children,
  style,
}: { icon?: ReactNode; children: ReactNode; style?: SerializedStyles }) => (
  <Box css={style}>
    {icon ? icon : null}
    <Text size="xl" weight={600} css={styleBubbleText}>
      {children}
    </Text>
  </Box>
);

const PAIN_POINTS_STYLES = [
  {
    zIndex: 4,
    bgColor: '#fffce4',
    rotation: -4.33,
    offsetX: -118,
    offsetY: 25,
    mobileOffsetX: -28,
    mobileOffsetY: 25,
  },
  {
    zIndex: 3,
    bgColor: '#ffffff',
    rotation: 2.64,
    offsetX: 81,
    offsetY: 92,
    mobileOffsetX: 5,
    mobileOffsetY: 87,
  },
  {
    zIndex: 2,
    bgColor: '#edf2ff',
    rotation: -4.09,
    offsetX: -93,
    offsetY: 157,
    mobileOffsetX: -7,
    mobileOffsetY: 141,
  },
  {
    zIndex: 1,
    bgColor: '#ffffff',
    rotation: 0,
    offsetX: 44,
    offsetY: 221,
    mobileOffsetX: 4,
    mobileOffsetY: 199,
  },
] as const;

export default PainPointsSection;

const styleBubble = (point: (typeof PAIN_POINTS_STYLES)[number]) =>
  css({
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 24,
    borderRadius: 12,
    left: `calc(50% + ${point.offsetX}px)`,
    top: point.offsetY,
    transform: `translateX(-50%) rotate(${point.rotation}deg)`,
    backgroundColor: point.bgColor,
    boxShadow: 'none',
    zIndex: point.zIndex,
    [desktopFirstMediaQuery.mobile]: {
      left: `calc(50% + ${point.mobileOffsetX}px)`,
      top: point.mobileOffsetY,
      padding: 16,
      gap: 8,
    },
  });

const styleIcon = css({
  fontSize: 24,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 16,
  },
});

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '100px 0',
  width: '100%',
  background: `url("${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/bg_problem_gradation.jpg") 50%  center no-repeat`,
  backgroundSize: 'cover',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 40,
  maxWidth: 1200,
  width: '100%',
});

const styleTitle = css({
  fontSize: 34,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 26,
  },
});

const styleBubblesWrapper = css({
  position: 'relative',
  width: 581,
  height: 316,
  [desktopFirstMediaQuery.mobile]: {
    width: 581,
    maxWidth: '100%',
    overflow: 'visible',
    height: 280,
  },
});

const styleBubbleText = css({
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 16,
  },
});
